import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  ClipboardCheck,
  FileText,
  FolderPlus,
  HelpCircle,
  Layers,
  Loader2,
  Pencil,
  Plus,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import AgreementsShell from "@/components/agreements/AgreementsShell";
import CategoryIcon from "@/components/agreements/CategoryIcon";
import DocumentCard from "@/components/agreements/DocumentCard";
import { AttorneyBadgeNote } from "@/components/agreements/LegalDisclaimer";
import CopyButton from "@/components/agreements/CopyButton";
import { CATEGORIES, CATEGORY_BY_ID } from "@/data/agreements/categories";
import { STAGES, STAGE_BY_ID } from "@/data/agreements/stages";
import { planByStage, planByCategory, planTemplates, planAttorneyLed, LEVELS } from "@/lib/agreements/recommend";
import { useAgreementProject } from "@/hooks/useAgreementProject";
import { planApi, eventsApi, intakeApi } from "@/lib/agreements/api";
import SaveAgreementsToProjectDialog from "@/components/agreements/SaveAgreementsToProjectDialog";

/**
 * DocumentPlan — route: /agreements/plan
 *
 * The plan is derived from the intake, not stored: the same answers always produce the same list.
 * Every document carries the reasons that put it there, and those reasons stay on screen.
 */

const LEVEL_META = {
  required: { label: "Required", tile: "border-red-200 bg-red-50 text-red-800", dot: "bg-red-500" },
  recommended: { label: "Recommended", tile: "border-pink-200 bg-pink-50 text-pink-800", dot: "bg-pink-500" },
  situational: { label: "Situational", tile: "border-slate-200 bg-slate-50 text-slate-700", dot: "bg-slate-400" },
};

const NOT_CONFIGURED = "projects_integration_not_configured";

/**
 * The projects integration is optional. The backend answers a 501 whose detail.error is
 * `projects_integration_not_configured` when there is no projects collection to attach to; the
 * 200 body is also checked in case a deployment reports it that way instead of failing.
 */
function isNotConfigured(payload) {
  const d = payload && typeof payload === "object" ? payload : {};
  if (d.configured === false) return true;
  if (d.error === NOT_CONFIGURED) return true;
  const detail = d.detail;
  if (typeof detail === "string" && detail.includes(NOT_CONFIGURED)) return true;
  if (detail && typeof detail === "object" && detail.error === NOT_CONFIGURED) return true;
  return false;
}

const NOT_CONFIGURED_MESSAGE =
  "The projects integration is not switched on for this workspace yet, so there is nowhere to attach the plan. Nothing is lost — your plan lives here and copies out as text.";


// ─────────────────────────────────────────────────────────────────────
// My Plan LIST view — shown when no `?project=<id>` param is in the URL.
// Each row is one Project & Brand Intake submission. Clicking Open
// routes to `/agreements/plan?project=<id>` which renders the existing
// detailed plan for that specific submission.
// ─────────────────────────────────────────────────────────────────────
function MyPlanList() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    intakeApi
      .listSubmissions()
      .then((r) => alive && setRows(Array.isArray(r?.intakes) ? r.intakes : []))
      .catch((e) => alive && setError(e?.message || "Could not load your plans."));
    return () => { alive = false; };
  }, []);

  return (
    <AgreementsShell>
      <div className="w-full py-8 md:py-10 space-y-6" data-testid="my-plan-list">
        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">My Plan</h1>
            <p className="text-sm text-slate-600 mt-1">
              Every Project &amp; Brand Intake you have submitted, saved as its own record.
              Open one to see its full plan and checklist.
            </p>
          </div>
          <Link to="/agreements/intake" data-testid="my-plan-new-submission">
            <Button size="sm" className="rounded-full">
              <Plus className="mr-1.5 h-4 w-4" /> New submission
            </Button>
          </Link>
        </header>

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 text-red-800 text-sm px-3 py-2">
            {error}
          </div>
        )}

        {rows === null ? (
          <div className="text-sm text-slate-500 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading your plans…
          </div>
        ) : rows.length === 0 ? (
          <div
            className="rounded-lg border border-dashed border-slate-300 bg-white px-6 py-10 text-center"
            data-testid="my-plan-empty"
          >
            <p className="text-sm text-slate-700">You have not submitted a Project &amp; Brand Intake yet.</p>
            <Link to="/agreements/intake" className="mt-3 inline-block">
              <Button size="sm" className="rounded-full mt-3">
                <Plus className="mr-1.5 h-4 w-4" /> Start your first intake
              </Button>
            </Link>
          </div>
        ) : (
          <ul className="grid gap-3" data-testid="my-plan-rows">
            {rows.map((r) => (
              <li
                key={r.project_id}
                data-testid={`my-plan-row-${r.project_id}`}
                className="rounded-lg border border-slate-200 bg-white px-4 py-3 flex items-center justify-between gap-4 hover:border-primary/40 transition"
              >
                <div className="min-w-0">
                  <div className="text-base font-semibold text-slate-900 truncate">
                    {r.brand_name}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Project &amp; Brand Intake · Submitted
                    {r.updated_at ? ` · ${new Date(r.updated_at).toLocaleDateString()}` : ""}
                  </div>
                </div>
                <Link
                  to={`/agreements/plan?project=${encodeURIComponent(r.project_id)}`}
                  data-testid={`my-plan-open-${r.project_id}`}
                >
                  <Button size="sm" variant="outline" className="rounded-full">Open</Button>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </AgreementsShell>
  );
}


export default function DocumentPlan() {
  // With no `?project=` param → list every submission. Otherwise render
  // the existing detailed plan view for the specific project id.
  const [params] = useSearchParams();
  if (!params.get("project")) return <MyPlanList />;
  return <DocumentPlanDetail />;
}


function DocumentPlanDetail() {
  const { projectId, intake, completeness, plan, savePlan, loading } = useAgreementProject();
  const [groupBy, setGroupBy] = useState("stage");
  const [showReasons, setShowReasons] = useState(true);
  // The Save button now opens a Project picker dialog so multiple
  // intake submissions can live under the same project without
  // overwriting each other.
  const [saveOpen, setSaveOpen] = useState(false);
  const [attachNote, setAttachNote] = useState(null);
  const brandName = (intake?.tradeName || "").trim() || (intake?.legalEntityName || "").trim() || "your brand";

  const items = plan?.items || [];
  const counts = plan?.counts || { required: 0, recommended: 0, situational: 0, total: 0 };

  const attorneyLed = useMemo(() => (plan ? planAttorneyLed(plan) : []), [plan]);
  const templated = useMemo(() => (plan ? planTemplates(plan) : []), [plan]);

  const groups = useMemo(() => {
    if (!plan) return [];
    if (groupBy === "category") {
      const byCat = planByCategory(plan);
      return CATEGORIES.filter((c) => (byCat[c.id] || []).length > 0).map((c) => ({
        key: c.id,
        title: c.name,
        blurb: c.blurb,
        categoryId: c.id,
        items: byCat[c.id],
      }));
    }
    const byStage = planByStage(plan);
    return STAGES.filter((s) => (byStage[s.id] || []).length > 0).map((s) => ({
      key: s.id,
      title: s.name,
      blurb: s.blurb,
      categoryId: null,
      items: byStage[s.id],
    }));
  }, [plan, groupBy]);

  const planText = useMemo(() => buildPlanText(items, counts, intake), [items, counts, intake]);

  // Called by the SaveAgreementsToProjectDialog after a successful pick.
  // Ensures the plan is stored server-side (the picker attaches by
  // reading the stored plan), then hands the actual project-picking
  // + attaching to the dialog itself.
  const onOpenSaveDialog = async () => {
    try { await savePlan(); } catch { /* dialog will surface any error */ }
    setSaveOpen(true);
  };
  const onSaved = () => {
    setAttachNote({ tone: "ok", text: "Plan saved to your project — open it from the Projects page under CPG Agreements." });
    eventsApi.track({ type: "attach_plan", projectId, total: counts.total });
  };

  /* ── Empty state ─────────────────────────────────────────────────────── */
  if (completeness === 0) {
    return (
      <AgreementsShell
        crumbs={[{ label: "My plan" }]}
        title="Your document plan"
        subtitle="Answer the intake and this page fills itself in: every agreement your brand needs, at the level it needs it, with the reason it is on the list."
      >
        {loading ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading your answers…
          </p>
        ) : (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-start gap-4 p-8">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-600 text-white">
                <Sparkles className="h-5 w-5" />
              </span>
              <div>
                <h2 className="text-lg font-semibold">Nothing to plan yet</h2>
                <p className="mt-1 max-w-xl text-sm text-muted-foreground">
                  The plan is built from your intake answers — how you manufacture, how you sell, who works on the brand
                  and what you have raised. It takes a few minutes and you can change any answer later.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button asChild className="bg-indigo-600 hover:bg-indigo-700">
                  <Link to="/agreements/intake">Start the intake</Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/agreements/bundles">Browse pre-built bundles instead</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </AgreementsShell>
    );
  }

  return (
    <AgreementsShell
      wide
      crumbs={[{ label: "My plan" }]}
      title="Your document plan"
      subtitle="Built from your intake answers by a fixed set of rules — no guesswork. Every document below names the answer that put it there."
      actions={
        <>
          <Button asChild variant="outline">
            <Link to={`/agreements/intake?project=${encodeURIComponent(projectId)}`} data-testid="my-plan-edit-answers">
              <Pencil className="mr-2 h-4 w-4" /> Edit answers
            </Link>
          </Button>
          <CopyButton text={planText} label="Copy plan" variant="outline" />
          <Button
            onClick={onOpenSaveDialog}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="my-plan-save-to-project-btn"
          >
            <FolderPlus className="mr-2 h-4 w-4" />
            Save to Project
          </Button>
        </>
      }
    >
      {/* Counts */}
      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Total documents" value={counts.total} tone="border-indigo-200 bg-indigo-50 text-indigo-900" />
        {LEVELS.map((level) => (
          <StatTile key={level} label={LEVEL_META[level].label} value={counts[level] ?? 0} tone={LEVEL_META[level].tile} />
        ))}
      </div>

      {attachNote && (
        <div
          className={cn(
            "mb-5 rounded-xl border p-4 text-sm",
            attachNote.tone === "ok" && "border-emerald-200 bg-emerald-50 text-emerald-900",
            attachNote.tone === "info" && "border-slate-200 bg-slate-50 text-slate-700",
            attachNote.tone === "error" && "border-pink-300 bg-pink-50 text-pink-900",
          )}
        >
          {attachNote.text}
        </div>
      )}

      {items.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-sm text-muted-foreground">
            Your answers did not match any documents yet — that usually means the intake is only part-answered.{" "}
            <Link className="underline" to="/agreements/intake">
              Finish the intake
            </Link>{" "}
            and the list will fill in.
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Attorney-led */}
          {attorneyLed.length > 0 && (
            <section className="mb-5 rounded-xl border border-pink-300 bg-pink-50 p-4">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-pink-900">
                <AlertTriangle className="h-4 w-4 text-pink-600" />
                {attorneyLed.length} of these are attorney-led
              </h2>
              <p className="mt-1 text-xs leading-relaxed text-pink-900/90">
                These are not documents to paper yourself. Read the entries so you arrive prepared and know what you are
                agreeing to — then have counsel draft or review them.
              </p>
              <ul className="mt-3 flex flex-wrap gap-1.5">
                {attorneyLed.map((item) => (
                  <li key={item.docId}>
                    <Link
                      to={`/agreements/library/${item.docId}`}
                      className="inline-flex rounded-md border border-pink-300 bg-white/70 px-2 py-1 text-xs text-pink-900 hover:bg-white"
                    >
                      {item.doc?.name || item.docId}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Templates available */}
          {templated.length > 0 && (
            <section className="mb-6 rounded-xl border bg-card p-4">
              <h2 className="flex items-center gap-2 text-sm font-semibold">
                <FileText className="h-4 w-4 text-indigo-600" />
                {templated.length} of your documents have a draftable template
              </h2>
              <p className="mt-1 text-xs text-muted-foreground">
                Each one comes with a Founder Guide and pre-fills the blanks from your intake. They are starting points
                for your attorney, not finished agreements.
              </p>
              <ul className="mt-3 flex flex-wrap gap-1.5">
                {templated.map((item) => (
                  <li key={item.docId}>
                    <Link
                      to={`/agreements/template/${item.docId}`}
                      className="inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs hover:bg-muted"
                    >
                      <FileText className="h-3 w-3 text-muted-foreground" />
                      {item.doc?.name || item.docId}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Controls */}
          <div className="mb-4 flex flex-col gap-3 border-y py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground">Group by</span>
              <div className="inline-flex rounded-lg border p-0.5">
                {[
                  ["stage", "Stage", Layers],
                  ["category", "Category", FolderPlus],
                ].map(([value, label, Icon]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setGroupBy(value)}
                    aria-pressed={groupBy === value}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium",
                      groupBy === value ? "bg-indigo-600 text-white" : "text-muted-foreground hover:bg-muted",
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" /> {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant={showReasons ? "default" : "outline"}
                onClick={() => setShowReasons((v) => !v)}
                className={cn(showReasons && "bg-indigo-600 hover:bg-indigo-700")}
              >
                <HelpCircle className="mr-1.5 h-4 w-4" />
                {showReasons ? "Why is this here? — showing" : "Why is this here?"}
              </Button>
              <Button asChild variant="outline" size="sm">
                <Link to="/agreements/checklist">
                  <ClipboardCheck className="mr-1.5 h-4 w-4" /> Track these on the checklist
                </Link>
              </Button>
            </div>
          </div>

          {/* Groups */}
          <div className="space-y-8">
            {groups.map((group) => (
              <section key={group.key}>
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  {group.categoryId && <CategoryIcon categoryId={group.categoryId} size="sm" />}
                  <h2 className="text-base font-semibold">{group.title}</h2>
                  <Badge variant="outline" className="font-normal">
                    {group.items.length} document{group.items.length === 1 ? "" : "s"}
                  </Badge>
                </div>
                {group.blurb && <p className="mb-3 max-w-2xl text-xs text-muted-foreground">{group.blurb}</p>}
                <div className="grid gap-3 md:grid-cols-2">
                  {group.items.map((item) => {
                    // DocumentCard shows the first reason inline; anything beyond it goes
                    // underneath so the whole `reasons` array is on screen, never hidden.
                    const extra = showReasons ? (item.reasons || []).slice(1) : [];
                    return (
                      <div key={item.docId} className="flex flex-col">
                        <DocumentCard
                          doc={item.doc}
                          level={item.level}
                          reasons={showReasons ? item.reasons : undefined}
                          className="flex-1"
                        />
                        {extra.length > 0 && (
                          <div className="mt-1.5 rounded-lg border border-dashed bg-muted/40 px-2.5 py-2">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                              Also because
                            </p>
                            <ul className="mt-1 space-y-1">
                              {extra.map((reason, i) => (
                                <li key={i} className="flex gap-1.5 text-xs text-muted-foreground">
                                  <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-muted-foreground/50" />
                                  <span>{reason}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>

          {/* A last honest word */}
          <div className="mt-8">
            <AttorneyBadgeNote
              level="yes"
              note="This plan tells you which agreements your situation calls for. It does not tell you what any of them should say for your specific deal — that part is a conversation with counsel."
            />
          </div>
        </>
      )}
      <SaveAgreementsToProjectDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        intakeProjectId={projectId}
        brandName={brandName}
        onSaved={onSaved}
      />
    </AgreementsShell>
  );
}

function StatTile({ label, value, tone }) {
  return (
    <div className={cn("rounded-xl border p-3", tone)}>
      <p className="text-2xl font-bold tabular-nums leading-none">{value}</p>
      <p className="mt-1.5 text-xs font-medium">{label}</p>
    </div>
  );
}

/** Plain-text export of the whole plan, reasons included. */
function buildPlanText(items, counts, intake) {
  const lines = [];
  lines.push("MY CPG DOCUMENT PLAN");
  if (intake?.tradeName) lines.push(`Brand: ${intake.tradeName}`);
  const stageName = STAGE_BY_ID[intake?.stage]?.name;
  if (stageName) lines.push(`Stage: ${stageName}`);
  lines.push(
    `${counts.total} documents — ${counts.required} required, ${counts.recommended} recommended, ${counts.situational} situational`,
  );
  lines.push("");
  lines.push(
    "Educational and informational only. Not legal advice. Have important agreements reviewed by a qualified attorney before signing.",
  );
  lines.push("");

  for (const level of LEVELS) {
    const group = items.filter((i) => i.level === level);
    if (group.length === 0) continue;
    lines.push("─".repeat(70));
    lines.push(`${LEVEL_META[level].label.toUpperCase()} (${group.length})`);
    lines.push("");
    for (const item of group) {
      const cat = CATEGORY_BY_ID[item.doc?.category]?.short || item.doc?.category || "";
      lines.push(`• ${item.doc?.name || item.docId}${cat ? `  [${cat}]` : ""}`);
      for (const reason of item.reasons || []) lines.push(`    why: ${reason}`);
      if (item.doc?.attorneyReview === "strongly-recommended") lines.push("    attorney involvement strongly recommended");
      if (item.doc?.templateId) lines.push("    template available");
      lines.push("");
    }
  }
  return lines.join("\n");
}
